import { Button, Container } from "@chakra-ui/react";
import styles from "./contact.module.scss";
import InputText from "../../components/contact/InputText";
import InputRadio from "../../components/contact/InputRadio";
import { useState, useRef } from "react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const ContactForm = () => {
  const [inputData, setInputData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    agree: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { elementRef: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });
  const { elementRef: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.2 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate consent checkbox
    if (!inputData.agree) {
      setSubmitStatus('error');
      setErrorMessage('Please agree to the privacy policy');
      return;
    }

    // Get reCAPTCHA token
    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      setSubmitStatus('error');
      setErrorMessage('reCAPTCHA verification failed. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...inputData,
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }

      setSubmitStatus('success');
      // Reset form
      setInputData({
        name: "",
        email: "",
        subject: "",
        message: "",
        agree: false,
      });
      recaptchaRef.current?.reset();
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    // Handle checkbox separately
    const inputValue = type === 'checkbox' ? checked : value;
    setInputData((prev) => ({ ...prev, [name]: inputValue }));
  };

  return (
    <div className={`sectionContainer ${styles.contactContainer}`} id="contact">
      <Container>
        <h2
          ref={titleRef}
          className={`sectionSubtitle ${styles.subtitle} fade-in ${titleVisible ? 'visible' : ''}`}
        >
          get in touch
        </h2>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={`${styles.form} fade-in ${formVisible ? 'visible' : ''}`}
        >
          <div className={styles.formRow}>
            <InputText
              onChange={handleChange}
              icon="user"
              label="name"
              name="name"
              value={inputData.name}
            />
            <InputText
              onChange={handleChange}
              icon="mail"
              label="email"
              name="email"
              type="email"
              value={inputData.email}
            />
          </div>
          <InputText
            onChange={handleChange}
            icon="pen"
            label="subject"
            name="subject"
            value={inputData.subject}
          />
          <InputText
            onChange={handleChange}
            icon="chat"
            label="message"
            name="message"
            value={inputData.message}
            multiline
          />
          <InputRadio
            onChange={handleChange}
            value={inputData.agree}
            name="agree"
            label=" I hereby agree that BioLean may use the information I have provided in the contact form above to contact me regarding the subject specified and acknowledge that BioLean will not use the provided information for any other purpose."
          />

          {/* reCAPTCHA - invisible */}
          {RECAPTCHA_SITE_KEY && (
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={RECAPTCHA_SITE_KEY}
            />
          )}

          {/* Status messages - positioned between checkbox and button */}
          <div className={styles.messageContainer}>
            {submitStatus === 'success' && (
              <div className={styles.successMessage}>
                Thank you! Your message has been sent successfully.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className={styles.errorMessage}>
                {errorMessage || 'Failed to send message. Please try again.'}
              </div>
            )}
          </div>

          <div className={styles.buttonContainer}>
            <Button
              variant="outline"
              size="lg"
              className={styles.formButton}
              type="submit"
              disabled={isSubmitting || !RECAPTCHA_SITE_KEY}
            >
              {isSubmitting ? 'Sending...' : 'send message'}
            </Button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default ContactForm;
